import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Chip,
  Spinner,
} from '@heroui/react'
import { useState } from 'react'

import {
  AnalyticsService,
  type TextAnalyticsResult,
  type TextAnalyticsEntry,
} from '../services'
import { SimilarityAnalysis } from '../SimilarityAnalysis'

import { DatabaseAnalytics } from './DatabaseAnalytics'
import { RecommendationAnalytics } from './RecommendationAnalytics'

export function AnalyticsViewer() {
  const [textAnalyticsData, setTextAnalyticsData] =
    useState<TextAnalyticsResult | null>(null)
  const [textAnalyticsLoading, setTextAnalyticsLoading] = useState(false)
  const [textAnalyticsError, setTextAnalyticsError] = useState<string | null>(
    null
  )

  const handleGetTextAnalytics = async () => {
    setTextAnalyticsLoading(true)
    setTextAnalyticsError(null)

    try {
      const result = await AnalyticsService.getTextAnalytics()
      setTextAnalyticsData(result)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to get analytics'
      setTextAnalyticsError(errorMessage)
      console.error('Text analytics error:', error)
    } finally {
      setTextAnalyticsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Recommendation Analytics - Full Width */}
      <RecommendationAnalytics />

      {/* Database Analytics - Full Width */}
      <DatabaseAnalytics />

      {/* Text Analytics */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="text-2xl">📝</div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                  Text Content Analytics
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Detailed analysis of text content in GIF documents
                </p>
              </div>
            </div>
            <Button
              color="primary"
              variant="flat"
              size="sm"
              onPress={handleGetTextAnalytics}
              isLoading={textAnalyticsLoading}
              isDisabled={textAnalyticsLoading}
            >
              {textAnalyticsLoading ? 'Analyzing...' : 'Analyze Text Content'}
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {textAnalyticsError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-600 dark:text-red-400 text-sm">
                {textAnalyticsError}
              </p>
            </div>
          )}

          {!textAnalyticsData &&
            !textAnalyticsLoading &&
            !textAnalyticsError && (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">📝</div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  Text Content Analysis
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-6">
                  Analyze text content from all GIF documents with detailed
                  insights including character counts, emotion distribution, and
                  content samples.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Chip size="sm" variant="flat" color="primary">
                    Text Samples
                  </Chip>
                  <Chip size="sm" variant="flat" color="success">
                    Character Counts
                  </Chip>
                  <Chip size="sm" variant="flat" color="secondary">
                    Emotion Analysis
                  </Chip>
                </div>
              </div>
            )}

          {textAnalyticsLoading && (
            <div className="text-center py-12">
              <Spinner size="lg" />
              <p className="mt-4 text-slate-600 dark:text-slate-400">
                Analyzing text content...
              </p>
            </div>
          )}

          {textAnalyticsData && (
            <div className="space-y-6">
              {/* Summary Statistics */}
              <div>
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                  Summary Statistics
                </h4>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {textAnalyticsData.totalCount.toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Documents with Text
                    </div>
                  </div>
                  <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {textAnalyticsData.totalCharacters.toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Total Characters
                    </div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {Math.round(
                        textAnalyticsData.totalCharacters /
                          textAnalyticsData.totalCount
                      )}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Avg Characters
                    </div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {AnalyticsService.calculateTextAnalyticsSummary(
                        textAnalyticsData
                      ).totalWords.toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Total Words
                    </div>
                  </div>
                </div>
              </div>

              {/* Text Content Samples */}
              <div>
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                  Text Content Samples ({textAnalyticsData.entries.length}{' '}
                  total)
                </h4>
                <div className="max-h-96 overflow-y-auto space-y-3">
                  {textAnalyticsData.entries
                    .slice(0, 20)
                    .map((entry: TextAnalyticsEntry) => (
                      <div
                        key={entry.id}
                        className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Chip size="sm" variant="flat" color="primary">
                            {entry.coreEmotion}
                          </Chip>
                          <span className="text-xs text-slate-500">
                            {entry.characterCount} characters
                          </span>
                          <span className="text-xs text-slate-400">
                            ID: {entry.id.slice(0, 8)}...
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 font-medium">
                          &ldquo;{entry.text}&rdquo;
                        </p>
                      </div>
                    ))}
                  {textAnalyticsData.entries.length > 20 && (
                    <div className="text-center py-4">
                      <p className="text-sm text-slate-500">
                        Showing first 20 of {textAnalyticsData.entries.length}{' '}
                        entries
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Emotional Tag Similarity Analysis */}
      <SimilarityAnalysis />
    </div>
  )
}
